import { cloneDeep } from 'lodash';

import {
  buyerService,
  columnsFetchService
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import {
  GET_BUYERS,
  GET_BUYER,
  SET_WIDGET_TITLE,
  RESET_BUYER_CALL_COUNTER,
  GET_BUYER_REFERENCES,
  TOGGLE_BUYER_PERMISSION,
  DELETE_BUYER,
  UPDATE_BUYER,
  CREATE_BUYER,
  SAVE_BUYER,
  HANDLE_LINK_RPC,
  SET_INVITES_FOR_BUYER,
  SET_SHOW_NEW_BUYER_INVITES
} from './actionTypes';
import {
  SET_BUYERS,
  SET_BUYER_REFERENCES,
  SET_CHECK_REFERENCES_TEXT,
  SET_WIDGET_TITLE as SET_WIDGET_TITLE_MUTATION,
  SET_USER_VISIBLE_COLUMNS,
  SET_BUYER
} from './mutationTypes';

import {
  SET_ACTION_STATUS_ERROR,
  SET_ACTION_STATUS_PENDING,
  SET_ACTION_STATUS_SUCCESS,
} from '@/store/helpers';

const actions = {
  /**
   * Get all buyers
   */
  async [SET_WIDGET_TITLE]({ commit }, { title }) {
    commit(SET_WIDGET_TITLE_MUTATION, title);
  },

  async [GET_BUYERS]({ rootState, state, commit }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_BUYERS });
    commit(SET_BUYERS, []);

    try {
      const response = await buyerService.getBuyers(
        rootState.currentAccountId,
        true
      );
      const buyers = response.data.buyers;
      const columns = columnsFetchService.getColumnList(
        {},
        buyerService.defaultColumns,
        'buyers',
        state.columnSettings
      );
      commit(SET_BUYERS, buyers);
      commit(SET_USER_VISIBLE_COLUMNS, columns);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_BUYERS });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_BUYERS, error });
    }
  },
  /**
   * Get a buyer
   */
  async [GET_BUYER]({ rootState, commit, state }, { buyerId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_BUYER });
    commit(SET_BUYERS, []);

    try {
      const response = await buyerService.getBuyer(
        rootState.currentAccountId,
        buyerId
      );
      const buyers = [response.data.buyer];
      const columns = columnsFetchService.getColumnList(
        {},
        buyerService.defaultColumns,
        'buyers',
        state.columnSettings
      );
      commit(SET_BUYERS, buyers);
      commit(SET_USER_VISIBLE_COLUMNS, columns);
      commit(SET_WIDGET_TITLE_MUTATION, buyers[0].name);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_BUYER });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: GET_BUYER, error });
    }
  },
  /**
   * Reset buyer call counter
   */
  async [RESET_BUYER_CALL_COUNTER]({ rootState, commit }, { buyerId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: RESET_BUYER_CALL_COUNTER });
    try {
      await buyerService.resetCallCounters(rootState.currentAccountId, buyerId);
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: RESET_BUYER_CALL_COUNTER
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: RESET_BUYER_CALL_COUNTER,
        error
      });
    }
  },

  /**
   * Get buyer references
   */
  async [GET_BUYER_REFERENCES]({ rootState, commit }, { buyerId }) {
    commit(
      SET_CHECK_REFERENCES_TEXT,
      'DELETE_BUYER_MODAL_CHECK_ASSOCIATION_TEXT'
    );
    commit(SET_ACTION_STATUS_PENDING, { actionName: GET_BUYER_REFERENCES });
    commit(SET_BUYER_REFERENCES, null);
    try {
      const response = await buyerService.getBuyerReferences(
        rootState.currentAccountId,
        buyerId
      );
      commit(
        SET_BUYER_REFERENCES,
        buyerService.updateBuyerReferencesStateName(response.data)
      );
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: GET_BUYER_REFERENCES });
    } catch (error) {
      commit(
        SET_CHECK_REFERENCES_TEXT,
        'DELETE_BUYER_MODAL_CHECK_ASSOCIATION_ERROR_TEXT'
      );
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: GET_BUYER_REFERENCES,
        error
      });
    }
  },
  /**
   * Toggle buyer permission
   */
  async [TOGGLE_BUYER_PERMISSION](
    { rootState, commit, state },
    { buyer, permissionProperty }
  ) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: TOGGLE_BUYER_PERMISSION });
    try {
      const buyerToUpdate = cloneDeep(buyer);
      const request = {
        id: buyerToUpdate.id,
        [permissionProperty]: !buyerToUpdate[permissionProperty]
      };
      const response = await buyerService.updateBuyer(
        rootState.currentAccountId,
        request
      );
      buyerToUpdate[permissionProperty] =
        response.data.buyer[permissionProperty];
      const index = state.buyers.findIndex(b => b.id === buyerToUpdate.id);
      commit(SET_BUYER, { index, buyer: buyerToUpdate });
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: TOGGLE_BUYER_PERMISSION
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, {
        actionName: TOGGLE_BUYER_PERMISSION,
        error
      });
    }
  },
  /**
   * Delete a buyer
   */
  async [DELETE_BUYER]({ rootState, commit, state }, { buyerId }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: DELETE_BUYER });
    try {
      await buyerService.deleteBuyer(rootState.currentAccountId, buyerId, true);

      const arrayToModify = cloneDeep(state.buyers);

      arrayToModify.splice(
        state.buyers.findIndex(b => b.id === buyerId),
        1
      );

      commit(SET_BUYERS, arrayToModify);
      commit(SET_ACTION_STATUS_SUCCESS, { actionName: DELETE_BUYER });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: DELETE_BUYER, error });
    }
  },
  /**
   * Update a buyer
   */
  async [UPDATE_BUYER]({ rootState, commit, dispatch }, { buyer, rpcItem }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: UPDATE_BUYER });
    try {
      await buyerService.updateBuyer(rootState.currentAccountId, buyer);

      await dispatch(HANDLE_LINK_RPC, { buyer, rpcItem });

      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: UPDATE_BUYER
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: UPDATE_BUYER, error });
    }
  },
  /**
   * Create a buyer
   */
  async [CREATE_BUYER]({ rootState, commit, dispatch }, { buyer, rpcItem }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: CREATE_BUYER });
    try {
      const response = await buyerService.createBuyer(
        rootState.currentAccountId,
        buyer
      );

      await dispatch(HANDLE_LINK_RPC, { buyer: response.data.buyers, rpcItem });

      await dispatch(SAVE_BUYER, { buyer: response.data.buyers });

      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: CREATE_BUYER,
        data: response.data.buyers
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: CREATE_BUYER, error });
    }
  },
  async [HANDLE_LINK_RPC]({ rootState, commit }, { buyer, rpcItem }) {
    commit(SET_ACTION_STATUS_PENDING, { actionName: HANDLE_LINK_RPC });
    const buyerId = buyer && buyer.id;
    if (!buyerId) return;

    const hasBuAndLink = rpc =>
      rpc.substr(0, 2) === 'BU' && rpc.substr(-4) === 'LINK';

    try {
      if (rpcItem && rpcItem.id) {
        if (
          !hasBuAndLink(rpcItem.id) &&
          (!buyer.rpc || rpcItem.id !== buyer.rpc.id)
        ) {
          await buyerService.linkToItem(rootState.currentAccountId, buyerId, {
            memberName: 'RPC',
            memberId: rpcItem.id
          });
        }
      } else {
        if (buyer.rpc && buyer.rpc.id && !hasBuAndLink(buyer.rpc.id)) {
          await buyerService.unlinkItem(rootState.currentAccountId, buyerId, {
            memberName: 'RPC',
            memberId: buyer.rpc.id
          });
        }
      }
      commit(SET_ACTION_STATUS_SUCCESS, {
        actionName: HANDLE_LINK_RPC
      });
    } catch (error) {
      commit(SET_ACTION_STATUS_ERROR, { actionName: HANDLE_LINK_RPC, error });
    }
  },
  async [SAVE_BUYER]({ commit }, { buyer }) {
    commit(SET_INVITES_FOR_BUYER, { buyer });
    commit(SET_SHOW_NEW_BUYER_INVITES, { showNewBuyerInvites: true });
  },
  [SET_INVITES_FOR_BUYER]({ commit }, { buyer }) {
    commit(SET_INVITES_FOR_BUYER, { buyer });
  },
  [SET_SHOW_NEW_BUYER_INVITES]({ commit }, { showNewBuyerInvites }) {
    commit(SET_SHOW_NEW_BUYER_INVITES, { showNewBuyerInvites });
  }
};

export default actions;
